import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import socketIO from "socket.io-client";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { format } from "timeago.js";
import { Link } from "react-router-dom";
import { AiOutlineSend } from "react-icons/ai";
import { TfiGallery } from "react-icons/tfi";
import { toast } from "react-toastify";
import { backend_url, server } from "../../server";
import { IMAGE_UPLOAD_MAX_BYTES, IMAGE_UPLOAD_MAX_MB } from "../../uploadLimits";
import { isRedundantOfferConversationText } from "../../utils/offerConversationMessages";
import {
  getPartnerFromCache,
  rememberPartner,
  rememberPartnerMissing,
  fallbackPartnerProfile,
  fetchPublicUserInfoDeduped,
} from "../../utils/conversationPartnerCache";
import SocialProfileBadges from "../shared/SocialProfileBadges";
import { addTocart } from "../../redux/actions/cart";
import { fetchLiveProductStock } from "../../utils/productStock";
import { buildCartLineForAdd } from "../../utils/offerPricing";
import { scrollInboxPaneIntoView } from "../../utils/inboxMobileScroll";
import {
  isDeliveredPromptActionable,
  formatDeliveredPromptEligibleDate,
} from "../../utils/deliveryEstimate";
import { useNotifications } from "../../hooks/useNotifications";

/**
 * Normalize Mongo / string ids for reliable comparisons.
 */
function normUserId(v) {
  if (v == null || v === "") return "";
  if (typeof v === "object" && v !== null) {
    if (v._id != null) return String(v._id);
    if (v.$oid) return String(v.$oid);
  }
  return String(v).trim();
}

function currentUserIdSet(user) {
  const s = new Set();
  if (!user) return s;
  const a = normUserId(user._id);
  const b = normUserId(user.id);
  if (a) s.add(a);
  if (b) s.add(b);
  return s;
}

const SELLER_PLACED_FALLBACK =
  "A new order is linked to this chat. The buyer's payment is protected until they confirm delivery — use this thread for shipping updates and questions.";
const SELLER_DISPATCHED_FALLBACK =
  "The buyer was notified that this order is on its way.";
const SELLER_DELIVERED_FALLBACK =
  "The buyer can confirm receipt or report a problem from this chat. You will be paid after they confirm or when the confirmation period ends.";

function inferOrderChatRole(sys, user, chat = null) {
  const bid = normUserId(sys?.buyerId);
  const sid = normUserId(sys?.sellerId);
  const myIds = currentUserIdSet(user);
  let isBuyer = Boolean(bid && myIds.has(bid));
  let isSeller = Boolean(sid && myIds.has(sid));

  const members = (chat?.members || []).map(normUserId).filter(Boolean);
  const myMemberId = members.find((id) => myIds.has(id));
  if (!isBuyer && !isSeller && myMemberId && bid && members.includes(bid) && members.length >= 2) {
    if (myMemberId === bid) isBuyer = true;
    else isSeller = true;
  }
  return { isBuyer, isSeller };
}

/**
 * Role-specific title/body for order_update system messages (buyer vs seller).
 * @param {object|null} user - Redux user (uses _id and/or id)
 * @param {object|null} chat - current conversation (members) when ids in systemData are unreliable
 */
function getOrderUpdateDisplay(sys, user, fallbackText, chat = null) {
  const { isBuyer, isSeller } = inferOrderChatRole(sys, user, chat);

  const event = String(sys?.event || "");

  let headline = sys?.title || fallbackText || "Order update";
  let copy = sys?.body;

  if (event === "placed") {
    if (isBuyer) {
      headline = sys.buyerTitle || sys.title || headline;
      copy = sys.buyerBody != null && sys.buyerBody !== "" ? sys.buyerBody : sys.body ?? copy;
    } else if (isSeller) {
      headline = sys.sellerTitle || sys.title || "New order";
      copy =
        sys.sellerBody != null && sys.sellerBody !== ""
          ? sys.sellerBody
          : sys.body != null && sys.body !== ""
            ? sys.body
            : SELLER_PLACED_FALLBACK;
    }
  } else if (event === "dispatched") {
    if (isBuyer) {
      headline = sys.buyerTitle || sys.title || headline;
      copy = sys.buyerBody != null && sys.buyerBody !== "" ? sys.buyerBody : sys.body ?? copy;
    } else if (isSeller) {
      headline = sys.sellerTitle || sys.title || "Marked as shipped";
      copy =
        sys.sellerBody != null && sys.sellerBody !== ""
          ? sys.sellerBody
          : sys.body != null && sys.body !== ""
            ? sys.body
            : SELLER_DISPATCHED_FALLBACK;
    }
  } else if (event === "delivered_prompt") {
    if (isBuyer) {
      headline = sys.buyerTitle || sys.title || headline;
      copy = sys.buyerBody != null && sys.buyerBody !== "" ? sys.buyerBody : sys.body ?? copy;
    } else if (isSeller) {
      headline = sys.sellerTitle || sys.title || "Awaiting buyer confirmation";
      copy =
        sys.sellerBody != null && sys.sellerBody !== ""
          ? sys.sellerBody
          : sys.body != null && sys.body !== ""
            ? sys.body
            : SELLER_DELIVERED_FALLBACK;
    }
  } else if (event === "review_prompt") {
    if (isBuyer) {
      headline = sys.buyerTitle || sys.title || headline;
      copy = sys.buyerBody != null && sys.buyerBody !== "" ? sys.buyerBody : sys.body ?? copy;
    } else if (isSeller) {
      headline = sys.sellerTitle || sys.title || "Rate your buyer";
      copy =
        sys.sellerBody != null && sys.sellerBody !== ""
          ? sys.sellerBody
          : sys.body != null && sys.body !== ""
            ? sys.body
            : "Leave a review — their review of you appears once you both submit.";
    }
  } else if (isBuyer && (sys?.buyerTitle || (sys?.buyerBody != null && sys.buyerBody !== ""))) {
    if (sys.buyerTitle) headline = sys.buyerTitle;
    if (sys.buyerBody != null && sys.buyerBody !== "") copy = sys.buyerBody;
  } else if (isSeller && (sys?.sellerTitle || (sys?.sellerBody != null && sys.sellerBody !== ""))) {
    if (sys.sellerTitle) headline = sys.sellerTitle;
    if (sys.sellerBody != null && sys.sellerBody !== "") copy = sys.sellerBody;
  } else if (isSeller && !isBuyer) {
    headline = sys.sellerTitle || headline;
    copy = sys.sellerBody != null && sys.sellerBody !== "" ? sys.sellerBody : copy;
  }

  const orderHref =
    sys?.orderId && isBuyer
      ? `/user/order/${sys.orderId}`
      : sys?.orderId && isSeller
        ? `/profile/seller-order/${sys.orderId}`
        : sys?.orderId
          ? `/user/order/${sys.orderId}`
          : null;

  return { isBuyer, isSeller, headline, copy, orderHref };
}

// Socket endpoint - use environment variable or fallback
const getSocketEndpoint = () => {
  if (typeof window === "undefined") return null;
  const envUrl = process.env.REACT_APP_SOCKET_URL;
  if (envUrl) return envUrl;
  
  // Check if we're in development
  if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
    return "http://localhost:4000";
  }
  
  // Production fallback
  return "wss://sock.veteranairsoft.com/";
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "£0.00";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(value));
};

// Stable components outside InboxPanel so the counter input doesn't remount on every keystroke (focus loss fix)
const SellerOfferActions = ({ offer, showCounter, counterInputValue, setCounterInputValue, onOpenCounter, onCloseCounter, onSubmitCounter, onAccept, onReject }) => {
  if (offer.status === "accepted") {
    return (
      <div className="text-xs text-emerald-600 font-semibold">
        ✓ Offer Accepted - Final Price: ${(offer.finalPrice || offer.counterPrice || offer.offeredPrice)?.toFixed(2)}
      </div>
    );
  }
  if (offer.status === "rejected") {
    return (
      <div className="text-xs text-rose-600 font-semibold">
        ✗ Offer Rejected
      </div>
    );
  }
  if (offer.status === "countered") {
    return (
      <div className="text-xs text-sky-600 font-semibold">
        Countered: ${offer.counterPrice?.toFixed(2)} - Waiting for buyer response
      </div>
    );
  }
  if (offer.status !== "pending") {
    return (
      <div className="text-xs text-gray-500">
        Status: <span className="font-semibold capitalize">{offer.status}</span>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2 w-full min-w-0">
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => onAccept(offer._id)} className="px-3 py-1.5 text-xs font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition">Accept</button>
        <button type="button" onClick={() => onReject(offer._id)} className="px-3 py-1.5 text-xs font-medium rounded-md bg-rose-600 text-white hover:bg-rose-700 transition">Reject</button>
        <button type="button" onClick={onOpenCounter} className="px-3 py-1.5 text-xs font-medium rounded-md bg-sky-600 text-white hover:bg-sky-700 transition">Counter</button>
      </div>
      {showCounter && (
        <div className="flex flex-col gap-2 w-full min-w-0 sm:flex-row sm:items-center">
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={counterInputValue}
            onChange={(e) => setCounterInputValue(e.target.value)}
            placeholder="Enter counter price"
            className="w-full min-w-0 flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-sky-500"
          />
          <div className="flex gap-2 w-full sm:w-auto shrink-0">
            <button
              type="button"
              onClick={() => {
                if (!counterInputValue || Number(counterInputValue) <= 0) {
                  toast.error("Please enter a valid counter price");
                  return;
                }
                onSubmitCounter(Number(counterInputValue));
              }}
              className="flex-1 sm:flex-none px-3 py-1.5 text-xs font-medium rounded-md bg-sky-600 text-white hover:bg-sky-700 transition"
            >
              Submit
            </button>
            <button type="button" onClick={onCloseCounter} className="flex-1 sm:flex-none px-3 py-1.5 text-xs font-medium rounded-md bg-gray-300 text-gray-700 hover:bg-gray-400 transition">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

const BuyerOfferActions = ({ offer, messages = [], showCounter, counterInputValue, setCounterInputValue, onOpenCounter, onCloseCounter, onSubmitCounter, onAcceptCounter, onAddToCart }) => {
  const isRedeemed = Boolean(offer.redeemedAt);
  const isAccepted = !isRedeemed && (offer.status === "accepted" ||
    (messages && messages.some(msg => {
      if (!msg.text) return false;
      const msgText = msg.text.toLowerCase();
      if (msgText.includes('offer accepted') || msgText.includes('accepted')) {
        const offerPrice = offer.offeredPrice || offer.finalPrice || offer.counterPrice;
        if (offerPrice && msg.text.includes(offerPrice.toFixed(2))) return true;
        const msgTime = new Date(msg.createdAt || 0).getTime();
        if (Date.now() - msgTime < 5 * 60 * 1000) return true;
      }
      return false;
    })));

  if (isRedeemed && offer.status === "accepted") {
    const finalPrice = offer.finalPrice || offer.counterPrice || offer.offeredPrice;
    return (
      <div className="flex flex-col gap-2">
        <div className="text-xs text-gray-600 font-semibold">
          Offer used (purchased){finalPrice ? ` — was ${formatCurrency(finalPrice)}` : ""}
        </div>
        <p className="text-[11px] text-gray-500">
          Submit a new offer if you want another discounted price on this item.
        </p>
      </div>
    );
  }

  if (isAccepted) {
    const finalPrice = offer.finalPrice || offer.counterPrice || offer.offeredPrice;
    return (
      <div className="flex flex-col gap-2">
        <div className="text-xs text-emerald-600 font-semibold mb-2">✓ Offer Accepted - Final Price: ${finalPrice?.toFixed(2)}</div>
        <button type="button" onClick={() => onAddToCart(offer)} className="px-3 py-1.5 text-xs font-medium rounded-md bg-[#38513b] text-white hover:bg-[#2f4232] transition">Add to Cart</button>
        <p className="text-[11px] text-emerald-700">You can add more quantity at this price from the product page or cart.</p>
      </div>
    );
  }
  if (offer.status === "rejected") {
    return <div className="text-xs text-rose-600 font-semibold">✗ Offer Rejected</div>;
  }
  if (offer.status === "countered") {
    return (
      <div className="flex flex-col gap-2 w-full min-w-0">
        <div className="text-xs text-sky-600 font-semibold mb-2">Seller Countered: ${offer.counterPrice?.toFixed(2)}</div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => onAcceptCounter(offer)} className="px-3 py-1.5 text-xs font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition">Accept Counter</button>
          <button type="button" onClick={onOpenCounter} className="px-3 py-1.5 text-xs font-medium rounded-md bg-sky-600 text-white hover:bg-sky-700 transition">Counter Back</button>
        </div>
        {showCounter && (
          <div className="flex flex-col gap-2 w-full min-w-0 sm:flex-row sm:items-center mt-2">
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={counterInputValue}
              onChange={(e) => setCounterInputValue(e.target.value)}
              placeholder="Enter your counter price"
              className="text-black w-full min-w-0 flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
            <div className="flex gap-2 w-full sm:w-auto shrink-0">
              <button
                type="button"
                onClick={() => {
                  if (!counterInputValue || Number(counterInputValue) <= 0) {
                    toast.error("Please enter a valid counter price");
                    return;
                  }
                  onSubmitCounter(counterInputValue);
                }}
                className="flex-1 sm:flex-none px-3 py-1.5 text-xs font-medium rounded-md bg-sky-600 text-white hover:bg-sky-700 transition"
              >
                Submit
              </button>
              <button type="button" onClick={onCloseCounter} className="flex-1 sm:flex-none px-3 py-1.5 text-xs font-medium rounded-md bg-gray-300 text-gray-700 hover:bg-gray-400 transition">Cancel</button>
            </div>
          </div>
        )}
      </div>
    );
  }
  if (offer.status === "pending") {
    return <div className="text-xs text-amber-600 font-semibold">⏳ Waiting for seller response</div>;
  }
  return null;
};

const InboxPanel = ({ initialConversationId, initialOfferId }) => {
  const { user } = useSelector((state) => state.user);
  const { cart } = useSelector((state) => state.cart);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const socketRef = useRef(null);
  const [conversations, setConversations] = useState([]);
  const [conversationUsers, setConversationUsers] = useState({});
  const [arrivalMessage, setArrivalMessage] = useState(null);
  const [currentChat, setCurrentChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [images, setImages] = useState();
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [offers, setOffers] = useState([]);
  const [loadingOffers, setLoadingOffers] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [offerPrice, setOfferPrice] = useState("");
  const [isSubmittingOffer, setIsSubmittingOffer] = useState(false);
  const [counteringOfferId, setCounteringOfferId] = useState(null);
  const [counterInputValue, setCounterInputValue] = useState("");
  /** Locks delivery-prompt CTAs as soon as the buyer taps an option (prevents double-submit). Cleared on confirm-received API error only. */
  const [deliveredPromptLockedOrders, setDeliveredPromptLockedOrders] = useState({});
  const deliveredPromptLockRef = useRef(new Set());
  const scrollRef = useRef(null);
  const inboxChatPaneRef = useRef(null);
  const deepLinkedInboxScrollKeyRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const lastMessageCountRef = useRef(0);
  
  // Get notifications hook to mark message notifications as read when conversation is opened
  const { notifications, markAsRead, fetchUnreadCount } = useNotifications();

  const updateConversationSummary = (conversationId, lastMessage, lastMessageId) => {
    if (!conversationId) return;
    setConversations((prev) =>
      prev.map((conversation) =>
        conversation._id === conversationId
          ? {
              ...conversation,
              lastMessage,
              lastMessageId,
            }
          : conversation
      )
    );
  };

  // Helper function to normalize conversationId (handle both string and object)
  const normalizeConversationId = (conversationId) => {
    if (!conversationId) return null;
    if (typeof conversationId === 'string') return conversationId;
    if (typeof conversationId === 'object' && conversationId._id) return conversationId._id.toString();
    return conversationId.toString();
  };

  // Detect if user is a seller
  const isSeller = user?.isSeller || user?.role === "Seller" || user?.shopId;

  const fetchConversations = useCallback(async () => {
    if (!user?._id) return;
    try {
      // Use seller endpoint if user is a seller, otherwise use buyer endpoint
      const endpoint = isSeller 
        ? `${server}/conversation/get-all-conversation-seller/${user._id}`
        : `${server}/conversation/get-all-conversation-user/${user._id}`;
      const response = await axios.get(endpoint, {
        withCredentials: true,
      });
      setConversations(response.data.conversations || []);
    } catch (error) {
      // Error fetching conversations
    }
  }, [user?._id, isSeller]);

  const fetchMessages = useCallback(
    async (conversationId) => {
      const targetId = conversationId || currentChat?._id;
      if (!targetId) return;
      try {
        const response = await axios.get(
          `${server}/message/get-all-messages/${targetId}`
        );
        const serverMessages = response.data.messages || [];
        
        // Update message count ref for polling comparison
        lastMessageCountRef.current = serverMessages.length;
        
        // Simply set messages from server - optimistic messages will be replaced
        // This is fine because the server should have the message by the time we refresh
        setMessages(serverMessages);
      } catch (error) {
        // Error handled silently
      }
    },
    [currentChat?._id]
  );

  // Initialize socket connection only when user is available
  useEffect(() => {
    if (!user?._id) return;

    const endpoint = getSocketEndpoint();
    if (!endpoint) return;

    // Create socket connection with error handling
    const socket = socketIO(endpoint, {
      transports: ["polling", "websocket"],
      reconnection: true,
      reconnectionAttempts: 3,
      reconnectionDelay: 1000,
      timeout: 20000,
      autoConnect: true,
    });

    // Suppress connection errors in console
    socket.on("connect_error", (error) => {
      // Silently handle connection errors
    });

    socket.on("connect", () => {
      const uid = String(user._id || user.id || "").trim();
      if (uid) socket.emit("addUser", uid);
    });

    socket.on("getMessage", (data) => {
      // Only process if we have conversationId or can determine it
      if (data.conversationId || data.senderId) {
        setArrivalMessage({
          sender: data.senderId,
          text: data.text,
          images: data.images,
          createdAt: Date.now(), // Will be replaced by server timestamp when we fetch
          conversationId: data.conversationId,
        });
        
        // If message mentions an offer, refresh offers list
        const messageText = data.text?.toLowerCase() || '';
        if (messageText.includes('offer') || messageText.includes('made an offer')) {
          // Small delay to ensure offer is saved to database
          setTimeout(() => {
            fetchOffers();
          }, 500);
        }
      }
    });

    socket.on("getUsers", (data) => {
      setOnlineUsers(data);
    });
    
    // Listen for conversation updates (when last message changes)
    socket.on("getLastMessage", (data) => {
      if (data.lastMessage && data.lastMessagesId) {
        // Find the conversation and update it
        setConversations((prev) =>
          prev.map((conversation) => {
            // Update if this conversation matches (check by lastMessageId)
            if (conversation.members?.includes(data.lastMessagesId)) {
              return {
                ...conversation,
                lastMessage: data.lastMessage,
                lastMessageId: data.lastMessagesId,
                updatedAt: new Date(),
              };
            }
            return conversation;
          })
        );
        // Refresh offers when conversation is updated (might be a new offer)
        fetchOffers();
      }
    });

    socketRef.current = socket;

    return () => {
      if (socketRef.current) {
        socketRef.current.off("getMessage");
        socketRef.current.off("getUsers");
        socketRef.current.off("getLastMessage");
        socketRef.current.off("connect");
        socketRef.current.off("connect_error");
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [user?._id, user?.id]);

  useEffect(() => {
    if (!arrivalMessage) return;

    // Normalize conversation IDs for comparison
    const arrivalConversationId = normalizeConversationId(arrivalMessage.conversationId);
    const currentChatId = normalizeConversationId(currentChat?._id);
    
    // Compare normalized IDs (convert to strings for reliable comparison)
    const arrivalIdStr = String(arrivalConversationId || '');
    const currentIdStr = String(currentChatId || '');
    const isCurrentChatMessage = arrivalIdStr && currentIdStr && arrivalIdStr === currentIdStr;
    
    let refreshTimeout;
    
    // If message is for current chat, add it optimistically and refresh from server
    if (isCurrentChatMessage) {
      // Add message optimistically for immediate UI update
      setMessages((prev) => {
        // Check if message already exists (prevent duplicates)
        const messageExists = prev.some(msg => {
          // Check by sender, text/images, and timestamp
          const sameSender = String(msg.sender) === String(arrivalMessage.sender);
          const sameText = msg.text === arrivalMessage.text;
          const sameImages = msg.images === arrivalMessage.images;
          const timeDiff = Math.abs(
            new Date(msg.createdAt || 0).getTime() - 
            new Date(arrivalMessage.createdAt || 0).getTime()
          );
          return (sameSender && (sameText || sameImages) && timeDiff < 3000);
        });
        
        if (messageExists) {
          return prev;
        }
        
        return [...prev, arrivalMessage];
      });
      
      // Delay to ensure message is saved to database, then refresh to get server timestamp
      // The optimistic message will be visible immediately, then replaced with server data
      refreshTimeout = setTimeout(() => {
        fetchMessages(currentChatId);
      }, 800);
    }

    // Update conversation summary for the target conversation
    let targetConversationId = arrivalConversationId;

    if (!targetConversationId) {
      // Fallback: try to find conversation by sender
      const currentConversations = conversations;
      const match = currentConversations.find((conversation) =>
        conversation.members?.includes(arrivalMessage.sender)
      );
      targetConversationId = match?._id ? normalizeConversationId(match._id) : null;
    }

    if (targetConversationId) {
      updateConversationSummary(
        targetConversationId,
        arrivalMessage.text || (arrivalMessage.images ? "Photo" : ""),
        arrivalMessage.sender
      );
      
      // Refresh conversations list to update last message and timestamp
      fetchConversations();
      
      // If message mentions offer (creation, acceptance, rejection, counter), refresh offers
      const messageText = arrivalMessage.text?.toLowerCase() || '';
      if (messageText.includes('offer') || 
          messageText.includes('accepted') || 
          messageText.includes('rejected') ||
          messageText.includes('counter')) {
        // Small delay to ensure offer is saved/updated in database
        setTimeout(() => {
          fetchOffers();
        }, 500);
      }
    }
    
    // Clear arrival message after processing
    setArrivalMessage(null);
    
    return () => {
      if (refreshTimeout) clearTimeout(refreshTimeout);
    };
  }, [arrivalMessage, currentChat, conversations, fetchConversations, fetchMessages]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // When opening inbox with a conversation ID (e.g. from "message seller" on a product), refetch
  // so the conversation has the latest productId and the product card shows the correct product.
  // location.key changes on every navigate, so clicking "message seller" on another product
  // triggers a refetch even when the conversation ID is the same (same buyer-seller, new product).
  useEffect(() => {
    if (initialConversationId) {
      fetchConversations();
    }
  }, [initialConversationId, fetchConversations, location.key]);

  // Fetch offers - users can be both buyer and seller, so fetch from both endpoints
  const fetchOffers = useCallback(async () => {
    if (!user?._id) return;
    try {
      setLoadingOffers(true);
      let allOffers = [];
      const currentUserId = user._id.toString();
      
      // Fetch seller offers (offers made TO this user as a seller)
      try {
        const sellerResponse = await axios.get(`${server}/offer/seller/all`, {
          withCredentials: true,
        });
        const sellerOffers = (sellerResponse.data.offers || []).map(offer => ({
          ...offer,
          userRole: 'seller', // Mark as seller offer
        }));
        allOffers = [...allOffers, ...sellerOffers];
      } catch (error) {
        console.error('DEBUG: Error fetching seller offers', error);
      }
      
      // Fetch buyer offers (offers made BY this user as a buyer)
      try {
        const buyerResponse = await axios.get(`${server}/offer/my/all`, {
          withCredentials: true,
        });
        const buyerOffers = (buyerResponse.data.offers || []).map(offer => ({
          ...offer,
          userRole: 'buyer', // Mark as buyer offer
        }));
        allOffers = [...allOffers, ...buyerOffers];
      } catch (error) {
        console.error('DEBUG: Error fetching buyer offers', error);
      }
      
      // Normalize conversationId and userId in all offers
      const normalizedOffers = allOffers.map(offer => {
        // Normalize userId - keep as is if string, extract _id if object
        let normalizedUserId = offer.userId;
        if (offer.userId && typeof offer.userId === 'object' && offer.userId._id) {
          normalizedUserId = offer.userId._id.toString();
        } else if (offer.userId && typeof offer.userId === 'string') {
          normalizedUserId = offer.userId;
        }
        
        // Normalize shopId
        let normalizedShopId = offer.shopId;
        if (offer.shopId && typeof offer.shopId === 'object' && offer.shopId._id) {
          normalizedShopId = offer.shopId._id.toString();
        } else if (offer.shopId && typeof offer.shopId === 'string') {
          normalizedShopId = offer.shopId;
        }
        
        return {
          ...offer,
          conversationId: normalizeConversationId(offer.conversationId),
          userId: normalizedUserId,
          shopId: normalizedShopId,
        };
      });
      
      setOffers(normalizedOffers);
    } catch (error) {
      console.error('DEBUG: Error fetching offers', error);
      // Error fetching offers
    } finally {
      setLoadingOffers(false);
    }
  }, [user?._id]);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  // getUsers is already handled in the main socket useEffect above

  const onlineCheck = (chat) => {
    if (!chat?.members?.length || !user) return false;
    const myIds = currentUserIdSet(user);
    const partnerId = chat.members.map(normUserId).find((id) => id && !myIds.has(id));
    if (!partnerId) return false;
    return onlineUsers.some((item) => normUserId(item?.userId) === partnerId);
  };

  useEffect(() => {
    if (!currentChat?._id) return;
    fetchMessages(currentChat._id);
    // Refresh offers when conversation changes to ensure updated offers are shown
    fetchOffers();
  }, [currentChat?._id, fetchMessages, fetchOffers]);

  // True when the current conversation has at least one offer (then we show offer cards only, not "Product you're discussing")
  const hasOffersInCurrentConversation = useMemo(() => {
    if (!currentChat?._id || currentChat?.isAdminPriority || !offers?.length) return false;
    const chatId = currentChat._id.toString();
    const currentUserId = user?._id?.toString();
    return offers.some((offer) => {
      const offerConversationId = offer.conversationId?._id?.toString() || offer.conversationId?.toString();
      const offerUserId = typeof offer.userId === "string" ? offer.userId : (offer.userId?._id?.toString() || offer.userId?.toString() || "");
      const offerShopId = typeof offer.shopId === "string" ? offer.shopId : (offer.shopId?._id?.toString() || offer.shopId?.toString() || "");
      return offerConversationId === chatId && (offerUserId === currentUserId || offerShopId === currentUserId);
    });
  }, [currentChat?._id, currentChat?.isAdminPriority, offers, user?._id]);

  // Periodically refresh offers to catch new offers in real-time
  // This ensures offers appear even if socket events are missed
  useEffect(() => {
    if (!user?._id) return;
    
    // Refresh offers every 10 seconds when user is on inbox page
    const offersRefreshInterval = setInterval(() => {
      fetchOffers();
    }, 10000); // 10 seconds
    
    return () => {
      clearInterval(offersRefreshInterval);
    };
  }, [user?._id, fetchOffers]);

  // Smart polling as backup for real-time updates
  // Polls every 8 seconds for current chat only, lightweight and efficient
  useEffect(() => {
    if (!currentChat?._id) {
      lastMessageCountRef.current = 0;
      return;
    }
    
    // Initialize message count ref
    lastMessageCountRef.current = messages.length;
    
    let pollInterval;
    let abortController = new AbortController();
    const currentChatId = currentChat._id;
    
    // Lightweight polling function - only fetches messages for current chat
    const pollMessages = async () => {
      // Don't poll if tab is not visible (save resources)
      if (document.hidden) {
        return;
      }
      
      // Don't poll if component unmounted or chat changed
      if (!currentChatId || abortController.signal.aborted) {
        return;
      }
      
      try {
        const response = await axios.get(
          `${server}/message/get-all-messages/${currentChatId}`,
          { signal: abortController.signal }
        );
        const serverMessages = response.data.messages || [];
        
        // Only update if message count changed (avoid unnecessary re-renders)
        if (serverMessages.length !== lastMessageCountRef.current) {
          const previousCount = lastMessageCountRef.current;
          lastMessageCountRef.current = serverMessages.length;
          setMessages(serverMessages);
        }
      } catch (error) {
        // Ignore abort errors and network errors (they're expected)
        if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
          // Silently handle other errors
        }
      }
    };
    
    // Start polling with 8 second interval (lightweight, won't crash site)
    pollInterval = setInterval(pollMessages, 8000);
    
    // Also poll immediately after a short delay (in case socket missed a message)
    const initialPollTimeout = setTimeout(pollMessages, 2000);
    
    // Cleanup
    return () => {
      clearInterval(pollInterval);
      clearTimeout(initialPollTimeout);
      abortController.abort();
    };
  }, [currentChat?._id]);

  const handleConversationSelect = (conversation) => {
    if (!conversation) return;

    const partner = conversationUsers[conversation._id];

    setCurrentChat(conversation);
    setSelectedPartner(partner || null);

    scrollInboxPaneIntoView(inboxChatPaneRef.current, { desktopMinWidthPx: 1024 });
  };

  useEffect(() => {
    if (!currentChat?._id) return;

    const partner = conversationUsers[currentChat._id];
    if (partner) {
      setSelectedPartner(partner);
    } else {
      // Partner not loaded yet, trigger loading
      loadConversationPartner(currentChat).then((loadedPartner) => {
        if (loadedPartner) {
          setSelectedPartner(loadedPartner);
        }
      });
    }
  }, [currentChat?._id, conversationUsers]);

  useEffect(() => {
    if (!initialConversationId || !conversations.length) return;
    const match = conversations.find(
      (conversation) => conversation._id === initialConversationId
    );

    if (match) {
      setCurrentChat(match);
      const partner = conversationUsers[match._id];
      if (partner) {
        setSelectedPartner(partner);
      } else {
        // Partner not loaded yet, trigger loading
        loadConversationPartner(match).then((loadedPartner) => {
          if (loadedPartner) {
            setSelectedPartner(loadedPartner);
          }
        });
      }
      // Refresh offers when conversation is loaded from URL
      fetchOffers();
    }
  }, [initialConversationId, conversations, conversationUsers, fetchOffers]);

  // Deep link from offer notifications (legacy links or ?offer= when conversation id missing)
  useEffect(() => {
    if (initialConversationId || !initialOfferId || !offers.length || !conversations.length) {
      return;
    }
    const offer = offers.find((o) => String(o._id) === String(initialOfferId));
    if (!offer?.conversationId) return;
    const match = conversations.find(
      (conversation) => String(conversation._id) === String(offer.conversationId)
    );
    if (!match) return;

    setCurrentChat(match);
    const partner = conversationUsers[match._id];
    if (partner) {
      setSelectedPartner(partner);
    } else {
      loadConversationPartner(match).then((loadedPartner) => {
        if (loadedPartner) {
          setSelectedPartner(loadedPartner);
        }
      });
    }
  }, [
    initialConversationId,
    initialOfferId,
    offers,
    conversations,
    conversationUsers,
  ]);

  // Deep link (e.g. product page "Send message"): scroll the chat pane into view — page often stays at top under header/sidebar.
  useEffect(() => {
    if (!initialConversationId) return;
    if (!currentChat?._id || String(currentChat._id) !== String(initialConversationId)) return;

    const scrollKey = `${location.pathname}${location.search}|${location.key}`;
    if (deepLinkedInboxScrollKeyRef.current === scrollKey) return;
    deepLinkedInboxScrollKeyRef.current = scrollKey;

    return scrollInboxPaneIntoView(inboxChatPaneRef.current, { desktopMinWidthPx: 1024 });
  }, [initialConversationId, currentChat?._id, location.pathname, location.search, location.key]);

  // Mark message notifications as read when a conversation is opened (initiator or receiver).
  // Always mark unread message_received for the current conversation — no skip. This ensures
  // new messages that arrive while viewing are marked read, and receiver-side sync matches initiator.
  useEffect(() => {
    if (!currentChat?._id || !notifications.length) return;

    const conversationId = normalizeConversationId(currentChat._id);
    if (!conversationId) return;

    const conversationIdStr = String(conversationId);

    const unreadMessageNotifications = notifications.filter((notif) => {
      if (notif.read) return false;
      if (notif.type !== "message_received") return false;
      const notifRelatedId = notif.relatedId?.toString();
      return notifRelatedId === conversationIdStr;
    });

    if (unreadMessageNotifications.length === 0) return;

    const markPromises = unreadMessageNotifications
      .filter((notif) => notif._id)
      .map((notif) => markAsRead(notif._id));

    Promise.all(markPromises)
      .then(() => {
        fetchUnreadCount?.();
      })
      .catch((error) => {
        console.error("Error marking message notifications as read:", error);
      });
  }, [currentChat?._id, notifications, markAsRead, fetchUnreadCount]);

  // Helper function to scroll container to bottom
  const scrollToBottom = useCallback((smooth = false) => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      // Use requestAnimationFrame to ensure DOM is fully rendered
      requestAnimationFrame(() => {
        // Scroll to maximum scroll height (bottom)
        if (smooth) {
          container.scrollTo({
            top: container.scrollHeight,
            behavior: 'smooth'
          });
        } else {
          // Instant scroll for opening conversation - directly set scrollTop
          container.scrollTop = container.scrollHeight;
        }
      });
    }
  }, []);
  
  // Auto-scroll to bottom when conversation is opened or messages are loaded
  const prevChatIdRef = useRef(null);
  useEffect(() => {
    if (currentChat?._id) {
      const chatChanged = prevChatIdRef.current !== currentChat._id;
      prevChatIdRef.current = currentChat._id;
      
      // Scroll to bottom when conversation opens or when messages are loaded for this conversation
      if (chatChanged || messages.length > 0) {
        const timeoutId = setTimeout(() => {
          scrollToBottom(false); // Instant scroll when opening conversation
        }, 300); // Delay to ensure messages are fully rendered
        return () => clearTimeout(timeoutId);
      }
    }
  }, [currentChat?._id, messages.length, scrollToBottom]);
  
  // Only scroll when messages actually change (new message added), not on every render
  const prevMessagesLengthRef = useRef(0);
  useEffect(() => {
    if (!currentChat?._id || !messages.length) return;
    
    // Only scroll if a new message was added (length increased)
    if (messages.length > prevMessagesLengthRef.current) {
      // Use setTimeout to ensure DOM is updated before scrolling
      const timeoutId = setTimeout(() => {
        scrollToBottom(true); // Smooth scroll for new messages
      }, 100);
      prevMessagesLengthRef.current = messages.length;
      return () => clearTimeout(timeoutId);
    }
    prevMessagesLengthRef.current = messages.length;
  }, [messages.length, currentChat?._id, scrollToBottom]);

  const sendMessageHandler = async (event) => {
    event.preventDefault();
    if (!newMessage.trim() || !currentChat) return;

    // Prevent users from messaging themselves
    const receiverId = currentChat.members.find(
      (member) => member !== user?._id
    );
    
    if (!receiverId || String(user._id) === String(receiverId)) {
      toast.error("You cannot message yourself");
      return;
    }

    const message = {
      sender: user._id,
      text: newMessage,
      conversationId: currentChat._id,
    };

    if (socketRef.current?.connected) {
      socketRef.current.emit("sendMessage", {
        senderId: user?._id,
        receiverId,
        text: newMessage,
        conversationId: currentChat._id,
      });
    }

    try {
      const response = await axios.post(
        `${server}/message/create-new-message`,
        message,
        { withCredentials: true }
      );
      setMessages((prev) => [...prev, response.data.message]);
      setNewMessage("");
      updateLastMessage(message.text);
      updateConversationSummary(currentChat._id, message.text, user._id);
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error.message;
      if (errorMessage.includes("cannot message yourself")) {
        toast.error("You cannot message yourself");
      } else {
      }
    }
  };

  const updateLastMessage = async (text) => {
    if (!currentChat?._id) return;

    if (socketRef.current?.connected) {
      socketRef.current.emit("updateLastMessage", {
        lastMessage: text,
        lastMessageId: user._id,
      });
    }

    try {
      await axios.put(
        `${server}/conversation/update-last-message/${currentChat._id}`,
        {
          lastMessage: text,
          lastMessageId: user._id,
        }
      );
    } catch (error) {
      // Error handled silently
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !currentChat) return;

    // Validate file size
    if (file.size > IMAGE_UPLOAD_MAX_BYTES) {
      toast.error(`Image exceeds the ${IMAGE_UPLOAD_MAX_MB}MB size limit. Maximum upload size is ${IMAGE_UPLOAD_MAX_MB}MB per image.`);
      event.target.value = ""; // Reset input
      return;
    }

    // Prevent users from messaging themselves
    const receiverId = currentChat.members.find(
      (member) => member !== user._id
    );
    
    if (!receiverId || String(user._id) === String(receiverId)) {
      toast.error("You cannot message yourself");
      event.target.value = ""; // Reset input
      return;
    }

    setImages(file);

    const formData = new FormData();
    formData.append("images", file);
    formData.append("sender", user._id);
    formData.append("text", newMessage);
    formData.append("conversationId", currentChat._id);

    if (socketRef.current?.connected) {
      socketRef.current.emit("sendMessage", {
        senderId: user._id,
        receiverId,
        images: file,
      });
    }

    try {
      const response = await axios.post(
        `${server}/message/create-new-message`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        }
      );
      setImages(null);
      
      // Add message to state, but check for duplicates first
      setMessages((prev) => {
        const messageExists = prev.some(msg => 
          msg._id === response.data.message._id ||
          (msg.sender === response.data.message.sender && 
           msg.images === response.data.message.images &&
           Math.abs(new Date(msg.createdAt).getTime() - new Date(response.data.message.createdAt).getTime()) < 2000)
        );
        if (messageExists) return prev;
        return [...prev, response.data.message];
      });
      
      updateLastMessage("Photo");
      updateConversationSummary(currentChat._id, "Photo", user._id);
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error.message;
      if (errorMessage.includes("cannot message yourself")) {
        toast.error("You cannot message yourself");
      } else {
      }
    }
  };

  // Add to cart from conversation product card (fetch full product then add)
  const handleAddToCartFromProductCard = async (productId) => {
    if (!productId) {
      toast.error("Product not found");
      return;
    }
    try {
      const productResponse = await axios.get(`${server}/product/get-product/${productId}`);
      const product = productResponse.data?.product;
      if (!product) {
        toast.error("Product not found");
        return;
      }
      const isItemExists = cart && cart.find((i) => i._id === product._id);
      if (isItemExists) {
        toast.error("Item already in cart!");
        return;
      }
      if (product.stock < 1) {
        toast.error("Product stock limited!");
        return;
      }
      const cartData = { ...product, qty: 1, discountPrice: Number(product.discountPrice || 0) };
      dispatch(addTocart(cartData));
      toast.success("Item added to cart!");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to add to cart");
    }
  };

  // Send offer from inbox (when conversation is about a product and user is buyer)
  const handleSendOfferFromInbox = async (e) => {
    e.preventDefault();
    const product = currentChat?.productId;
    if (!product?._id) {
      toast.error("Product not found");
      return;
    }
    const numeric = Number(offerPrice);
    if (!numeric || numeric <= 0) {
      toast.error("Please enter a valid price");
      return;
    }
    try {
      setIsSubmittingOffer(true);
      await axios.post(
        `${server}/offer/create`,
        { productId: product._id, price: numeric },
        { withCredentials: true }
      );
      toast.success("Your offer has been sent to the seller");
      setShowOfferModal(false);
      setOfferPrice("");
      fetchOffers();
      if (currentChat?._id) fetchMessages(currentChat._id);
      fetchConversations();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Unable to submit offer");
    } finally {
      setIsSubmittingOffer(false);
    }
  };

  const publicConversations = useMemo(() => {
    return (conversations || [])
      .map((conversation) => {
        const partnerId = conversation.members.find(
          (member) => member !== user?._id
        );
        const partnerInfo = conversationUsers[conversation._id];
        return {
          ...conversation,
          partnerId,
          partnerInfo,
        };
      })
      .sort((a, b) => {
        // Sort by updatedAt descending (newest first)
        const aDate = new Date(a.updatedAt || a.createdAt || 0);
        const bDate = new Date(b.updatedAt || b.createdAt || 0);
        return bDate - aDate;
      });
  }, [conversations, conversationUsers, user?._id]);

  const loadConversationPartner = async (conversation) => {
    const partnerId = conversation.members.find(
      (member) => member !== user?._id
    );
    if (!partnerId) return null;

    const pid = String(partnerId);

    // If already loaded for this conversation row, return
    if (conversationUsers[conversation._id]) {
      return conversationUsers[conversation._id];
    }

    const applyPartnerPayload = (payload) => {
      setConversationUsers((prev) => ({
        ...prev,
        [conversation._id]: payload,
      }));
      if (currentChat?._id === conversation._id) {
        setSelectedPartner(payload);
      }
      return payload;
    };

    // Reuse prior lookups — avoids repeated 404s when accounts are deleted or lists re-render.
    const cachedPeer = getPartnerFromCache(pid);
    if (cachedPeer !== undefined) {
      const payload = cachedPeer === null ? fallbackPartnerProfile(pid) : cachedPeer;
      return applyPartnerPayload(payload);
    }

    try {
      // Check if this is an admin conversation
      if (conversation.isAdminPriority) {
        // Fetch admin info
        try {
          const adminResponse = await axios.get(
            `${server}/admin/info/${partnerId}`
          );
          const adminInfo = adminResponse.data.admin;
          if (adminInfo) {
            rememberPartner(pid, adminInfo);
            return applyPartnerPayload(adminInfo);
          }
        } catch (adminError) {
          if (process.env.NODE_ENV === "development") {
            console.warn("Failed to load admin info:", adminError?.response?.status, pid);
          }
        }
        rememberPartnerMissing(pid);
        return applyPartnerPayload(fallbackPartnerProfile(pid));
      }

      // If user is seller, they're talking to buyers (users)
      // If user is buyer, they're talking to sellers (shops)
      if (isSeller) {
        // Seller: fetch user info (buyer)
        try {
          const userInfo = await fetchPublicUserInfoDeduped(server, partnerId);
          if (userInfo) {
            return applyPartnerPayload(userInfo);
          }
          return applyPartnerPayload(fallbackPartnerProfile(pid));
        } catch {
          /* non-404 / network — leave uncached for retry */
        }
      } else {
        // Buyer: try shop info first, then user info
        try {
          const response = await axios.get(
            `${server}/shop/get-shop-info/${partnerId}`
          );
          const shop = response.data.shop;
          if (shop) {
            rememberPartner(pid, shop);
            return applyPartnerPayload(shop);
          }
        } catch (shopError) {
          // If shop doesn't exist, try to get user info instead
          try {
            const userInfo = await fetchPublicUserInfoDeduped(server, partnerId);
            if (userInfo) {
              return applyPartnerPayload(userInfo);
            }
            return applyPartnerPayload(fallbackPartnerProfile(pid));
          } catch {
            /* network etc. */
          }
        }
      }
    } catch (error) {
      // Error loading conversation partner
    }
    return null;
  };

  // Use ref to track loaded conversation IDs to prevent infinite loops
  const loadedConversationIdsRef = useRef(new Set());
  
  useEffect(() => {
    // Only load partners for conversations that haven't been loaded yet
    publicConversations.forEach((conversation) => {
      if (conversation._id && !loadedConversationIdsRef.current.has(conversation._id)) {
        loadedConversationIdsRef.current.add(conversation._id);
        loadConversationPartner(conversation);
      }
    });
    
    // Clean up: remove IDs that are no longer in conversations
    const currentIds = new Set(publicConversations.map(c => c._id).filter(Boolean));
    loadedConversationIdsRef.current.forEach((id) => {
      if (!currentIds.has(id)) {
        loadedConversationIdsRef.current.delete(id);
      }
    });
  }, [publicConversations]);

  // Memoize the conversation list to ensure it re-renders when notifications change
  // Include notifications in dependencies to ensure real-time updates
  const conversationList = useMemo(() => {
    if (!publicConversations.length) {
      return (
        <div className="w-full rounded-lg border border-dashed border-gray-200 p-6 text-center text-gray-500">
          <p className="text-base font-medium">No conversations yet.</p>
          <p className="text-sm mt-1">
            When you message a seller, the conversation will appear here.
          </p>
        </div>
      );
    }

    return publicConversations.map((conversation) => {
      const partner = conversation.partnerInfo;
      const lastMessagePreview = conversation.lastMessage || "No messages yet";
      const isActive = currentChat?._id === conversation._id;
      const isAdminPriority = !!conversation.isAdminPriority;
      // Always show "Administrator" for admin conversations, not the admin's personal name
      const partnerName = isAdminPriority ? "Administrator" : (partner?.username || partner?.name || "Seller");
      const adminRole = isAdminPriority && partner?.role ? partner.role : null;
      const avatarSrc = partner?.avatar
        ? `${backend_url}${partner.avatar}`
        : `${backend_url}default-avatar.png`;
      
      // Count unread notifications for this conversation
      // This will update in real-time when notifications change
      const conversationId = String(conversation._id);
      const unreadNotifications = notifications.filter((notif) => {
        if (notif.read) return false;
        if (notif.type !== "message_received") return false;
        const notifRelatedId = notif.relatedId?.toString();
        const matches = notifRelatedId === conversationId;
        return matches;
      });
      
      const unreadCount = unreadNotifications.length;

      const product = conversation?.productId;
      const isOrderThread =
        conversation?.contextType === "order" ||
        String(conversation?.contextKey || "").startsWith("order:");
      const orderLabel = isOrderThread
        ? conversation?.orderNumber != null
          ? `Order #${conversation.orderNumber}`
          : "Order chat"
        : null;
      const productName =
        typeof product === "object" && product !== null
          ? product?.name || "Product details unavailable"
          : "Product details unavailable";
      const productPrice =
        typeof product === "object" && product !== null
          ? Number(product?.discountPrice || 0)
          : null;

      return (
        <button
          key={conversation._id}
          type="button"
          className={`w-full flex items-start gap-3 rounded-lg border px-3 py-3 text-left transition relative ${
            isActive
              ? "border-[#38513b] bg-[#f5faf5]"
              : isAdminPriority
                ? "border-amber-300 bg-amber-50/50 hover:border-amber-400"
                : "border-transparent hover:border-gray-200"
          }`}
          onClick={() => handleConversationSelect(conversation)}
        >
          <span className="relative flex-shrink-0">
            <img
              src={avatarSrc}
              alt={partnerName}
              className="w-12 h-12 rounded-full object-contain"
            />
            {/* Online status indicator - positioned at top-right */}
            <span
              className={`absolute top-0 right-0 h-3 w-3 rounded-full border-2 border-white ${
                onlineCheck(conversation) ? "bg-green-400" : "bg-gray-300"
              }`}
            />
            {/* Notification badge for unread messages - positioned at bottom-right to avoid conflict */}
            {unreadCount > 0 && (
              <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-semibold text-white border-2 border-white z-10">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </span>
          <span className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-sm font-semibold text-gray-900 truncate">{partnerName}</p>
              <span className="flex items-center gap-1.5 flex-shrink-0">
                {isAdminPriority && adminRole && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-amber-400 text-amber-900 border border-amber-500">
                    {adminRole}
                  </span>
                )}
                {unreadCount > 0 && (
                  <span className="h-2 w-2 rounded-full bg-red-500" />
                )}
              </span>
            </div>
            <p className="text-xs text-slate-600 truncate">
              {isOrderThread ? (
                <>
                  <span className="font-medium text-[#38513b]">{orderLabel}</span>
                  <span className="text-slate-500"> · order updates &amp; messages</span>
                </>
              ) : (
                <>
                  Product:{" "}
                  <span className="font-medium text-slate-700">
                    {productName}
                    {productPrice !== null && productPrice > 0 ? ` • ${formatCurrency(productPrice)}` : ""}
                  </span>
                </>
              )}
            </p>
            <p className="text-xs text-gray-500 truncate">Message: {lastMessagePreview}</p>
          </span>
        </button>
      );
    });
  }, [publicConversations, notifications, currentChat, conversationUsers, onlineUsers, user?._id, backend_url, onlineCheck, normalizeConversationId]);

  const renderConversationList = () => conversationList;


  // Get offers for the current conversation partner
  // User can be both buyer and seller, so show offers where:
  // - User is buyer: offers where userId = currentUserId AND shopId = partnerId (seller)
  // - User is seller: offers where shopId = currentUserId AND userId = partnerId (buyer)
  const currentPartnerOffers = useMemo(() => {
    if (!offers.length) return [];
    
    const currentUserId = user?._id?.toString();
    if (!currentUserId) return [];
    
    const chatId = currentChat?._id ? String(currentChat._id) : "";

    // If no partner selected, show all user's offers (both as buyer and seller)
    if (!selectedPartner?._id) {
      return offers.filter((offer) => {
        const offerUserId = typeof offer.userId === 'string'
          ? offer.userId
          : (offer.userId?._id?.toString() || offer.userId?.toString() || '');
        const offerShopId = typeof offer.shopId === 'string'
          ? offer.shopId
          : (offer.shopId?._id?.toString() || offer.shopId?.toString() || '');
        
        // Show if user is the buyer (userId matches) OR user is the seller (shopId matches)
        const userInOffer = (offerUserId === currentUserId || offerShopId === currentUserId);
        if (!userInOffer) return false;
        if (!chatId) return true;
        const offerConversationId = offer.conversationId?._id?.toString() || offer.conversationId?.toString() || "";
        return offerConversationId === chatId;
      });
    }
    
    const partnerId = selectedPartner._id.toString();
    
    // Filter offers for this conversation partner
    return offers.filter((offer) => {
      const offerUserId = typeof offer.userId === 'string'
        ? offer.userId
        : (offer.userId?._id?.toString() || offer.userId?.toString() || '');
      const offerShopId = typeof offer.shopId === 'string'
        ? offer.shopId
        : (offer.shopId?._id?.toString() || offer.shopId?.toString() || '');
      
      // Show offers where:
      // 1. User is buyer talking to this seller: userId = currentUserId AND shopId = partnerId
      // 2. User is seller talking to this buyer: shopId = currentUserId AND userId = partnerId
      const partnerMatch =
        (offerUserId === currentUserId && offerShopId === partnerId) ||
        (offerShopId === currentUserId && offerUserId === partnerId);
      if (!partnerMatch) return false;
      if (!chatId) return true;
      const offerConversationId = offer.conversationId?._id?.toString() || offer.conversationId?.toString() || "";
      return offerConversationId === chatId;
    });
  }, [offers, selectedPartner?._id, user?._id, currentChat?._id]);

  // Handle counter offer (update existing offer, don't create new one)
  const handleCounterOffer = async (originalOffer, counterPrice) => {
    if (!counterPrice || Number(counterPrice) <= 0) {
      toast.error("Please enter a valid counter price");
      return;
    }

    if (!originalOffer._id) {
      toast.error("Invalid offer");
      return;
    }

    // Only allow countering if seller has countered (status is "countered")
    if (originalOffer.status !== "countered") {
      toast.error("You can only counter back when seller has countered your offer");
      return;
    }

    try {
      // Update existing offer instead of creating new one
      const response = await axios.put(
        `${server}/offer/buyer/counter/${originalOffer._id}`,
        {
          price: Number(counterPrice),
        },
        { withCredentials: true }
      );
      toast.success("Counter offer sent!");
      fetchOffers();
      // Refresh messages to show the new system message
      if (currentChat?._id) {
        fetchMessages(currentChat._id);
      }
      // Refresh conversations to update last message
      fetchConversations();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to send counter offer");
    }
  };

  // Handle accept counter offer (update existing offer to accepted)
  const handleAcceptCounter = async (offer) => {
    if (!offer._id) {
      toast.error("Invalid offer");
      return;
    }
    
    if (offer.status !== "countered") {
      toast.error("This offer cannot be accepted");
      return;
    }
    
    if (!offer.counterPrice) {
      toast.error("No counter price available to accept");
      return;
    }

    try {
      const response = await axios.put(
        `${server}/offer/buyer/accept-counter/${offer._id}`,
        {},
        { withCredentials: true }
      );
      toast.success("Counter offer accepted! You can now add it to cart.");
      fetchOffers(); // Refresh offers to show updated status
      fetchConversations(); // Refresh conversations to update last message
      // Refresh messages to show the new system message
      if (currentChat?._id) {
        fetchMessages(currentChat._id);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to accept counter offer");
    }
  };

  // Handle add to cart for accepted offers
  const handleAddToCart = async (offer) => {
    if (offer?.redeemedAt) {
      toast.error("This offer was already used on a purchase. Submit a new offer for another discounted price.");
      return;
    }
    const finalPrice = offer.finalPrice || offer.counterPrice || offer.offeredPrice;
    if (!finalPrice) {
      toast.error("No valid price available");
      return;
    }
    const normalizeEntityId = (value) => {
      if (!value) return "";
      if (typeof value === "string" || typeof value === "number") return String(value);
      if (typeof value === "object") return String(value?._id || value?.id || "");
      return "";
    };
    const fallbackConversationShopId = normalizeEntityId(selectedPartner?._id);
    const offerShopId = normalizeEntityId(offer?.shopId) || fallbackConversationShopId;

    try {
      // Bundle offer: add all bundled products with proportional price split.
      if (offer.isBundleOffer) {
        const bundleProducts = Array.isArray(offer.bundleProductIds) ? offer.bundleProductIds : [];
        if (!bundleProducts.length) {
          toast.error("Bundle products not found in offer");
          return;
        }

        const validProducts = bundleProducts.filter((p) => p && (p._id || p.id));
        if (!validProducts.length) {
          toast.error("Product IDs not found in bundle offer");
          return;
        }

        const enrichedBundleProducts = await Promise.all(
          validProducts.map(async (product) => {
            const raw = product?.postageFees ?? product?.postageFee ?? product?.shippingFee;
            if (raw !== undefined && raw !== null) {
              return { ...product, postageFees: Number(raw) };
            }
            const pid = String(product._id || product.id);
            const live = await fetchLiveProductStock(pid);
            if (live.product) {
              return {
                ...product,
                postageFees: Number(live.product.postageFees ?? 0),
                stock: live.stock,
              };
            }
            return { ...product, postageFees: 0 };
          })
        );

        const existingById = new Map((cart || []).map((i) => [String(i._id), i]));

        // Always re-apply negotiated prices to every line in the bundle (including items
        // already in cart after a partial removal reverted siblings to list price).
        const baseTotal = validProducts.reduce(
          (sum, p) => sum + Number(p.discountPrice || 0),
          0
        );
        let allocated = 0;
        const totalTarget = Number(finalPrice);

        enrichedBundleProducts.forEach((product, idx) => {
          const base = Number(product.discountPrice || 0);
          const productId = String(product._id || product.id);
          const existing = existingById.get(productId);
          const productShopId =
            normalizeEntityId(product?.shopId) ||
            normalizeEntityId(product?.shop) ||
            normalizeEntityId(product?.sellerId) ||
            normalizeEntityId(existing?.shopId) ||
            normalizeEntityId(existing?.shop) ||
            normalizeEntityId(existing?.sellerId) ||
            offerShopId;
          let assignedPrice = 0;
          if (idx === enrichedBundleProducts.length - 1) {
            assignedPrice = Number((totalTarget - allocated).toFixed(2));
          } else if (baseTotal > 0) {
            assignedPrice = Number(((base / baseTotal) * totalTarget).toFixed(2));
            allocated += assignedPrice;
          } else {
            assignedPrice = Number((totalTarget / enrichedBundleProducts.length).toFixed(2));
            allocated += assignedPrice;
          }

          const cartData = {
            ...product,
            _id: productId,
            qty: Number(existing?.qty || 1),
            discountPrice: assignedPrice > 0 ? assignedPrice : base,
            bundleOriginalPrice: Number(product.originalPrice ?? product.discountPrice ?? 0),
            shopId: productShopId || undefined,
            shop: product?.shop || offer?.shopId || existing?.shop || undefined,
            sellerId: productShopId || undefined,
            bundleOfferId: offer._id,
            isBundleItemFromOffer: true,
            postageFees: Number(product.postageFees ?? 0),
          };
          dispatch(addTocart(cartData));
        });

        toast.success(
          enrichedBundleProducts.length === 1
            ? "Bundle item added to cart!"
            : `${enrichedBundleProducts.length} bundle items added to cart with offer pricing!`
        );
        return;
      }

      let productId = null;
      if (typeof offer.productId === "string") {
        productId = offer.productId;
      } else if (typeof offer.productId === "object" && offer.productId !== null) {
        productId = offer.productId._id?.toString() || offer.productId._id || null;
      }

      if (!productId) {
        toast.error("Product ID not found in offer");
        return;
      }

      productId = String(productId).trim();
      if (!productId || productId === "null" || productId === "undefined") {
        toast.error("Invalid product ID");
        return;
      }

      const live = await fetchLiveProductStock(productId);
      if (!live.product) {
        toast.error(live.message || "Product not found");
        return;
      }
      if (!live.ok) {
        toast.error(live.message || "Product stock limited!");
        return;
      }

      const product = live.product;
      const normalizedProductShopId =
        normalizeEntityId(product.shopId) ||
        normalizeEntityId(product.shop) ||
        offerShopId;

      const listingPrice = Number(product.discountPrice || offer.originalPrice || 0);
      const acceptedUnitPrice = Number(finalPrice);
      const existing = cart?.find((i) => String(i._id) === String(product._id));
      const result = buildCartLineForAdd({
        product,
        stock: live.stock,
        qty: 1,
        listingPrice,
        acceptedUnitPrice,
        existingItem: existing,
        extraFields: {
          postageFees: Number(product.postageFees ?? 0),
          shopId: normalizedProductShopId || undefined,
          acceptedOfferId: offer._id,
        },
      });

      if (result.error === "stock") {
        toast.error(
          result.stock > 0
            ? `Only ${result.stock} available in stock.`
            : live.message || "Product stock limited!"
        );
        return;
      }
      if (result.error === "bundle_offer") {
        toast.error(
          "This item is in your cart as part of a bundle offer. Adjust quantity in the cart or remove it first."
        );
        return;
      }
      if (result.error === "different_price") {
        toast.error("This item is already in your cart at a different price.");
        return;
      }

      dispatch(addTocart(result.item));
      toast.success(
        result.merged
          ? `Updated to ${result.item.qty} at your accepted offer price.`
          : "Item added to cart successfully!"
      );
    } catch (error) {
      if (error?.response?.status === 404) {
        toast.error("Product not found. The product may have been removed.");
      } else {
        toast.error(error?.response?.data?.message || "Failed to add to cart");
      }
    }
  };

  // Handle seller offer response (accept, reject, counter)
  const handleSellerOfferResponse = async (offerId, status, counterPrice = null) => {
    try {
      await axios.put(
        `${server}/offer/seller/${offerId}`,
        { status, counterPrice },
        { withCredentials: true }
      );
      toast.success(`Offer ${status} successfully`);
      fetchOffers(); // Refresh offers
      fetchConversations(); // Refresh conversations to update last message
      // Refresh messages to show the new message created by seller response
      if (currentChat?._id) {
        fetchMessages(currentChat._id);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update offer");
    }
  };

  return (
    <div className="w-full bg-white rounded-[10px] shadow-sm p-4">
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="w-full lg:w-[320px] flex-shrink-0 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Conversations</h2>
          <div className="space-y-3 overflow-y-auto max-h-[70vh]" key={`conversations-${notifications.length}-${notifications.filter(n => !n.read && n.type === "message_received").length}-${Date.now()}`}>
            {renderConversationList()}
          </div>
        </div>

        <div
          ref={inboxChatPaneRef}
          className="flex min-h-[400px] max-h-[500px] flex-1 flex-col scroll-mt-28 rounded-[10px] border border-gray-100"
        >
          {currentChat ? (
            <>
              <div className={`flex items-center justify-between px-4 py-3 border-b border-gray-100 ${
                currentChat?.isAdminPriority ? "bg-amber-50/80 border-amber-200" : "bg-gray-50"
              }`}>
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  {!currentChat?.isAdminPriority && selectedPartner?._id && selectedPartner?.isSeller ? (
                    <Link
                      to={`/shop/${selectedPartner._id}`}
                      className="flex min-w-0 items-center gap-3 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#38513b]"
                    >
                      <img
                        src={
                          selectedPartner?.avatar
                            ? `${backend_url}${selectedPartner.avatar}`
                            : `${backend_url}default-avatar.png`
                        }
                        alt={selectedPartner?.username || selectedPartner?.name || "Seller"}
                        className="h-12 w-12 shrink-0 rounded-full object-cover hover:ring-2 hover:ring-[#38513b]"
                      />
                      <div className="min-w-0">
                        <p className="truncate text-base font-semibold text-gray-900 hover:underline">
                          {selectedPartner?.username || selectedPartner?.name || "Seller"}
                        </p>
                        <p className="text-xs text-[#38513b]">View seller profile</p>
                      </div>
                    </Link>
                  ) : (
                    <img
                      src={
                        currentChat?.isAdminPriority
                          ? selectedPartner?.avatar
                            ? `${backend_url}${selectedPartner.avatar}`
                            : `${backend_url}default-avatar.png`
                          : selectedPartner?.avatar
                            ? `${backend_url}${selectedPartner.avatar}`
                            : `${backend_url}default-avatar.png`
                      }
                      alt={
                        currentChat?.isAdminPriority
                          ? "Administrator"
                          : selectedPartner?.username ||
                            selectedPartner?.name ||
                            (selectedPartner?.isSeller ? "Seller" : "User")
                      }
                      className="h-12 w-12 shrink-0 rounded-full object-contain"
                    />
                  )}
                  <div className="min-w-0">
                    {!(selectedPartner?._id && selectedPartner?.isSeller && !currentChat?.isAdminPriority) && (
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-base font-semibold text-gray-900">
                          {currentChat?.isAdminPriority
                            ? "Administrator"
                            : selectedPartner?.username ||
                              selectedPartner?.name ||
                              (selectedPartner?.isSeller ? "Seller" : "User")}
                        </p>
                        {currentChat?.isAdminPriority && selectedPartner?.role && (
                          <span className="rounded border border-amber-500 bg-amber-400 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-amber-900">
                            {selectedPartner.role}
                          </span>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-gray-500">
                      {onlineCheck(currentChat)
                        ? "Active now"
                        : currentChat?.isAdminPriority
                          ? "Administrator chat"
                          : "Offline — replies when they're back"}
                    </p>
                    {!currentChat?.isAdminPriority ? (
                      <SocialProfileBadges
                        profiles={selectedPartner?.linkedSocialProfiles}
                        compact
                        className="mt-1"
                      />
                    ) : null}
                  </div>
                </div>
              </div>

              <div 
                ref={messagesContainerRef}
                className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
              >
                {/* Combined Messages and Offers Section; product card rendered as latest message at bottom */}
                {(() => {
                  // Don't show offers in admin conversations - admins don't make product offers
                  if (currentChat?.isAdminPriority) {
                    // For admin conversations: system messages as centered middle bar; regular messages with sender name
                    return messages.map((m) => {
                      if (m.isSystem) {
                        return (
                          <div key={m._id || `sys-${m.createdAt}-${m.text}`} className="flex justify-center my-3">
                            <div className="px-4 py-2 rounded-lg bg-slate-200/80 text-slate-700 text-sm text-center inline-block max-w-[85%]">
                              {m.text}
                              {m.createdAt && <span className="block text-xs text-slate-500 mt-1">{format(m.createdAt)}</span>}
                            </div>
                          </div>
                        );
                      }
                      const isMe = String(m.sender) === String(user?._id);
                      const adminName = m.senderAdminName || "Administrator";
                      return (
                        <div
                          key={m._id}
                          className={`flex w-full ${isMe ? "justify-end" : "justify-start"}`}
                        >
                          <div className={`max-w-[75%] space-y-1 ${isMe ? "items-end" : "items-start"} flex flex-col`}>
                            {!isMe && (
                              <p className="text-[11px] font-medium text-slate-500">{adminName}</p>
                            )}
                            {m.images && (
                              <img
                                src={`${backend_url}${m.images}`}
                                alt=""
                                className="rounded-lg max-h-48 object-contain"
                              />
                            )}
                            {m.text && (
                              <div
                                className={`px-4 py-2 rounded-lg text-sm ${
                                  isMe ? "bg-[#38513b] text-white" : "bg-gray-100 text-gray-900"
                                }`}
                              >
                                {m.text}
                              </div>
                            )}
                            <p className="text-[11px] text-gray-400">{format(m.createdAt)}</p>
                          </div>
                        </div>
                      );
                    });
                  }
                  
                  // Convert offers to message-like format
                  // Use updatedAt for offers so they appear as latest when seller responds
                  // For buyers: always show their offers in the conversation, even if partner matching fails
                  let offersToShow = currentPartnerOffers;
                  
                  // User can be both buyer and seller, so show offers for current conversation
                  // If currentPartnerOffers is empty, try to find offers by conversationId
                  // BUT: Don't show offers in admin conversations (admins don't make product offers)
                  if (offers.length > 0 && offersToShow.length === 0 && currentChat?._id && !currentChat?.isAdminPriority) {
                    const chatId = currentChat._id.toString();
                    const currentUserId = user?._id?.toString();
                    
                    // Find offers matching this conversation
                    const conversationOffers = offers.filter((offer) => {
                      const offerConversationId = offer.conversationId?._id?.toString() || offer.conversationId?.toString();
                      const offerUserId = typeof offer.userId === 'string'
                        ? offer.userId
                        : (offer.userId?._id?.toString() || offer.userId?.toString() || '');
                      const offerShopId = typeof offer.shopId === 'string'
                        ? offer.shopId
                        : (offer.shopId?._id?.toString() || offer.shopId?.toString() || '');
                      
                      // Show if conversation matches AND user is involved (as buyer or seller)
                      return offerConversationId === chatId && 
                             (offerUserId === currentUserId || offerShopId === currentUserId);
                    });
                    
                    if (conversationOffers.length > 0) {
                      offersToShow = conversationOffers;
                    }
                  }
                  
                  // Don't show offers in admin conversations
                  if (currentChat?.isAdminPriority) {
                    offersToShow = [];
                  }
                  
                  
                  // Remove duplicate offers (same product, same buyer, same seller)
                  // Keep only the most recent offer for each product+buyer+seller combination
                  const uniqueOffers = offersToShow.reduce((acc, offer) => {
                    const bundleKey = Array.isArray(offer.bundleProductIds)
                      ? offer.bundleProductIds.map((p) => (p?._id || p || "").toString()).sort().join(",")
                      : "";
                    const offerSubjectKey = offer.isBundleOffer
                      ? `bundle:${bundleKey}`
                      : `product:${offer.productId?._id || offer.productId}`;
                    const key = `${offerSubjectKey}_${offer.userId}_${offer.shopId}`;
                    const existing = acc.find(o => {
                      const oBundleKey = Array.isArray(o.bundleProductIds)
                        ? o.bundleProductIds.map((p) => (p?._id || p || "").toString()).sort().join(",")
                        : "";
                      const oSubjectKey = o.isBundleOffer
                        ? `bundle:${oBundleKey}`
                        : `product:${o.productId?._id || o.productId}`;
                      const oKey = `${oSubjectKey}_${o.userId}_${o.shopId}`;
                      return oKey === key;
                    });
                    
                    // Keep the most recent offer (by updatedAt or createdAt)
                    if (!existing) {
                      acc.push(offer);
                    } else {
                      const existingTime = new Date(existing.updatedAt || existing.createdAt || 0).getTime();
                      const currentTime = new Date(offer.updatedAt || offer.createdAt || 0).getTime();
                      if (currentTime > existingTime) {
                        const index = acc.indexOf(existing);
                        acc[index] = offer;
                      }
                    }
                    return acc;
                  }, []);
                  
                  const offerMessages = uniqueOffers.map((offer) => ({
                    _id: `offer_${offer._id}`,
                    type: "offer",
                    offer: offer,
                    // For buyers: sender is themselves (userId)
                    // For sellers: sender is the buyer (userId)
                    sender: offer.userId?._id || offer.userId || user?._id,
                    // Use updatedAt so updated offers appear as latest messages
                    createdAt: offer.updatedAt || offer.createdAt || new Date(),
                    isOffer: true,
                  }));


                  const messagesForTimeline = messages.filter(
                    (m) => !isRedundantOfferConversationText(m?.text)
                  );

                  // Combine and sort by timestamp (ascending - oldest first, latest at bottom)
                  // Offers use updatedAt so when seller responds, they appear as latest message
                  const allItems = [...messagesForTimeline, ...offerMessages].sort((a, b) => {
                    const timeA = new Date(a.createdAt || 0).getTime();
                    const timeB = new Date(b.createdAt || 0).getTime();
                    return timeA - timeB; // Sort ascending (oldest first, latest at bottom)
                  });

                  const productSellerId = currentChat?.productId?.shopId?._id ?? currentChat?.productId?.shopId;
                  const isBuyerInChat = !productSellerId || String(user?._id) !== String(productSellerId);

                  const DELIVERED_RESOLUTION_COPY = {
                    buyer_confirmed: "Receipt confirmed — payment has been released to the seller.",
                    auto_payment_released:
                      "The confirmation window ended — payment was released to the seller automatically.",
                    issue_selected: "You chose to report a problem. Continue on your order page.",
                    refund_requested: "A refund request was submitted for this order.",
                  };

                  const handleOrderAccept = async (orderId) => {
                    const oid = String(orderId);
                    if (!oid || deliveredPromptLockRef.current.has(oid)) return;
                    deliveredPromptLockRef.current.add(oid);
                    setDeliveredPromptLockedOrders((prev) => ({ ...prev, [oid]: true }));
                    try {
                      await axios.put(`${server}/order/confirm-received/${orderId}`, {}, { withCredentials: true });
                      toast.success("Thanks — payment has been released to the seller.");
                      if (currentChat?._id) fetchMessages(currentChat._id);
                    } catch (err) {
                      deliveredPromptLockRef.current.delete(oid);
                      setDeliveredPromptLockedOrders((prev) => {
                        const next = { ...prev };
                        delete next[oid];
                        return next;
                      });
                      toast.error(err?.response?.data?.message || "Could not confirm receipt");
                    }
                  };

                  const handleOrderRaiseIssue = async (orderId) => {
                    const oid = String(orderId);
                    if (!oid || deliveredPromptLockRef.current.has(oid)) return;
                    deliveredPromptLockRef.current.add(oid);
                    setDeliveredPromptLockedOrders((prev) => ({ ...prev, [oid]: true }));
                    try {
                      await axios.post(
                        `${server}/order/delivered-prompt-choose-issue/${orderId}`,
                        {},
                        { withCredentials: true }
                      );
                      if (currentChat?._id) fetchMessages(currentChat._id);
                    } catch (_) {
                      deliveredPromptLockRef.current.delete(oid);
                      setDeliveredPromptLockedOrders((prev) => {
                        const next = { ...prev };
                        delete next[oid];
                        return next;
                      });
                    }
                    navigate(`/user/order/${oid}?raiseIssue=1`);
                  };

                  return (
                  <>
                  {allItems.map((item) => {
                    // Product card message (sent when user clicks "Send message" from product page)
                    if (item.isProductCard && item.productCardData) {
                      const product = item.productCardData;
                      const isMe = String(item.sender) === String(user?._id);
                      // Sender of the product card is always the buyer (they clicked "Send message" from product page)
                      const isBuyerInChat = isMe;
                      return (
                        <div
                          key={item._id || `product-${item.createdAt}`}
                          className={`flex w-full ${isMe ? "justify-end" : "justify-start"}`}
                          ref={scrollRef}
                        >
                          <div className={`max-w-[85%] space-y-1 ${isMe ? "items-end" : "items-start"} flex flex-col`}>
                            <div className="w-full max-w-md px-4 py-3 rounded-lg border-2 border-[#38513b]/30 bg-[#38513b]/5">
                              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Product you&apos;re discussing</p>
                              <div className="flex gap-3">
                                <Link
                                  to={`/product/${product.productId}`}
                                  className="flex-shrink-0"
                                >
                                  {product.images?.[0] ? (
                                    <img
                                      src={`${backend_url}${product.images[0]}`}
                                      alt={product.name || "Product"}
                                      className="w-20 h-20 rounded-lg object-contain border border-gray-200"
                                    />
                                  ) : (
                                    <span className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 text-xs">No img</span>
                                  )}
                                </Link>
                                <div className="flex-1 min-w-0">
                                  <Link to={`/product/${product.productId}`} className="font-semibold text-gray-900 hover:text-[#38513b] block truncate">
                                    {product.name || "Product"}
                                  </Link>
                                  <p className="text-sm font-medium text-[#38513b] mt-0.5">
                                    £{(product.discountPrice ?? 0).toFixed(2)}
                                  </p>
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    {isBuyerInChat ? (
                                      <>
                                        <button
                                          type="button"
                                          onClick={() => handleAddToCartFromProductCard(product.productId)}
                                          className="px-3 py-1.5 text-xs font-medium rounded-md bg-[#38513b] text-white hover:bg-[#2f4232]"
                                        >
                                          Add to cart
                                        </button>
                                        <Link
                                          to={`/product/${product.productId}`}
                                          className="px-3 py-1.5 text-xs font-medium rounded-md border border-[#38513b] text-[#38513b] hover:bg-[#38513b]/10 inline-block"
                                        >
                                          Buy now
                                        </Link>
                                        <button
                                          type="button"
                                          onClick={() => setShowOfferModal(true)}
                                          className="px-3 py-1.5 text-xs font-medium rounded-md border border-amber-500 text-amber-700 bg-amber-50 hover:bg-amber-100"
                                        >
                                          Send offer
                                        </button>
                                      </>
                                    ) : (
                                      <Link
                                        to={`/product/${product.productId}`}
                                        className="px-3 py-1.5 text-xs font-medium rounded-md bg-[#38513b] text-white hover:bg-[#2f4232] inline-block"
                                      >
                                        View product
                                      </Link>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <p className="text-[11px] text-gray-400">
                              {format(item.createdAt)}
                            </p>
                          </div>
                        </div>
                      );
                    }
                    if (item.isOffer) {
                      // Render offer as a message
                      const offer = item.offer;
                      // Determine user's role for THIS specific offer
                      // User can be both buyer and seller, so check each offer individually
                      const offerUserId = typeof offer.userId === 'string'
                        ? offer.userId
                        : (offer.userId?._id?.toString() || offer.userId?.toString() || '');
                      const offerShopId = typeof offer.shopId === 'string'
                        ? offer.shopId
                        : (offer.shopId?._id?.toString() || offer.shopId?.toString() || '');
                      const currentUserId = user?._id?.toString();
                      
                      // User is the BUYER for this offer if offer.userId matches currentUserId
                      const userIsBuyerForThisOffer = offerUserId === currentUserId && offerUserId !== '';
                      // User is the SELLER for this offer if offer.shopId matches currentUserId
                      const userIsSellerForThisOffer = offerShopId === currentUserId && offerShopId !== '';
                      
                      // Determine alignment: buyer's offers right-aligned, seller's offers left-aligned
                      const isBuyerOffer = userIsBuyerForThisOffer;
                      
                      // Show buyer actions if user is the buyer for this offer
                      const showBuyerActions = userIsBuyerForThisOffer;
                      
                      // Show seller actions if user is the seller for this offer AND offer is not accepted
                      // Once accepted, seller should only see status, not action buttons
                      const showSellerActions = userIsSellerForThisOffer && offer.status !== "accepted";
                      
                      return (
                        <div
                          key={item._id}
                          className={`flex w-full ${
                            isBuyerOffer ? "justify-end" : "justify-start"
                          }`}
                          ref={scrollRef}
                        >
                          <div className={`w-full max-w-[85%] sm:max-w-[75%] min-w-0 space-y-1 ${isBuyerOffer ? "items-end" : "items-start"} flex flex-col`}>
                            <div
                              className={`w-full min-w-0 px-4 py-3 rounded-lg text-sm ${
                                isBuyerOffer
                                  ? "bg-[#38513b] text-white"
                                  : "bg-gray-100 text-gray-900"
                              } border-2 ${
                                isBuyerOffer
                                  ? "border-[#38513b]"
                                  : "border-blue-300"
                              }`}
                            >
                              {offer.isBundleOffer ? (
                                <div className="mb-2">
                                  <div className="flex-1 mb-2">
                                    <p className={`font-semibold mb-1 ${isBuyerOffer ? "text-white" : "text-gray-900"}`}>
                                      {`Bundle (${offer.bundleProductIds?.length || 0} items)`}
                                    </p>
                                    <p className={`text-xs ${isBuyerOffer ? "text-gray-200" : "text-gray-600"}`}>
                                      Original: ${offer.originalPrice?.toFixed(2)}
                                    </p>
                                  </div>
                                  <div className="grid grid-cols-1 gap-2">
                                    {(offer.bundleProductIds || []).map((bundleProduct, idx) => (
                                      <div
                                        key={`${bundleProduct?._id || idx}`}
                                        className={`flex items-center gap-2 rounded-md border px-2 py-1.5 ${
                                          isBuyerOffer
                                            ? "border-white/20 bg-white/10"
                                            : "border-gray-300 bg-white"
                                        }`}
                                      >
                                        {bundleProduct?.images?.[0] ? (
                                          <img
                                            src={`${backend_url}${bundleProduct.images[0]}`}
                                            alt={bundleProduct?.name || "Bundle item"}
                                            className="w-10 h-10 rounded object-contain flex-shrink-0"
                                          />
                                        ) : (
                                          <div className={`w-10 h-10 rounded flex items-center justify-center text-[10px] ${
                                            isBuyerOffer ? "bg-white/10 text-gray-200" : "bg-gray-100 text-gray-500"
                                          }`}>
                                            No img
                                          </div>
                                        )}
                                        <p className={`text-xs leading-tight line-clamp-2 ${
                                          isBuyerOffer ? "text-gray-100" : "text-gray-700"
                                        }`}>
                                          {bundleProduct?.name || "Product"}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-start gap-2 mb-2">
                                  {offer.productId?.images?.[0] && (
                                    <img
                                      src={`${backend_url}${offer.productId.images[0]}`}
                                      alt={offer.productId?.name}
                                      className="w-16 h-16 rounded object-contain"
                                    />
                                  )}
                                  <div className="flex-1">
                                    <p className={`font-semibold mb-1 ${isBuyerOffer ? "text-white" : "text-gray-900"}`}>
                                      {offer.productId?.name || "Product"}
                                    </p>
                                    <p className={`text-xs ${isBuyerOffer ? "text-gray-200" : "text-gray-600"}`}>
                                      Original: ${offer.originalPrice?.toFixed(2)}
                                    </p>
                                  </div>
                                </div>
                              )}
                              
                              <div className={`mt-2 space-y-1 pt-2 border-t ${isBuyerOffer ? "border-white/20" : "border-gray-300"}`}>
                                <p className={`text-xs ${isBuyerOffer ? "text-white" : "text-gray-700"}`}>
                                  <span className="font-medium">Offer:</span> ${offer.offeredPrice?.toFixed(2)}
                                </p>
                                {offer.counterPrice && (
                                  <p className={`text-xs ${isBuyerOffer ? "text-blue-200" : "text-blue-600"} font-medium`}>
                                    Counter: ${offer.counterPrice?.toFixed(2)}
                                  </p>
                                )}
                                {offer.finalPrice && (
                                  <p className={`text-xs ${isBuyerOffer ? "text-emerald-200" : "text-emerald-600"} font-semibold`}>
                                    Final Price: ${offer.finalPrice?.toFixed(2)}
                                  </p>
                                )}
                                {/* Only show status if not accepted (accepted status is shown in BuyerOfferActions) */}
                                {offer.status !== "accepted" && (
                                  <p className={`text-xs mt-2 font-semibold capitalize ${
                                    offer.status === "rejected" ? (isBuyerOffer ? "text-red-200" : "text-red-600") :
                                    offer.status === "countered" ? (isBuyerOffer ? "text-blue-200" : "text-blue-600") :
                                    (isBuyerOffer ? "text-yellow-200" : "text-yellow-600")
                                  }`}>
                                    Status: {offer.status}
                                  </p>
                                )}
                              </div>

                              {/* Show actions for buyers on their own offers */}
                              {showBuyerActions && (
                                <div className={`mt-3 pt-2 border-t ${isBuyerOffer ? "border-white/20" : "border-gray-300"}`}>
                                  <div className={isBuyerOffer ? "" : "bg-white p-2 rounded border border-gray-200 shadow-sm"}>
                                    <BuyerOfferActions
                                      offer={offer}
                                      messages={messages}
                                      showCounter={counteringOfferId === offer._id}
                                      counterInputValue={counterInputValue}
                                      setCounterInputValue={setCounterInputValue}
                                      onOpenCounter={() => { setCounteringOfferId(offer._id); setCounterInputValue(""); }}
                                      onCloseCounter={() => { setCounteringOfferId(null); setCounterInputValue(""); }}
                                      onSubmitCounter={(value) => { handleCounterOffer(offer, value); setCounteringOfferId(null); setCounterInputValue(""); }}
                                      onAcceptCounter={handleAcceptCounter}
                                      onAddToCart={handleAddToCart}
                                    />
                                  </div>
                                </div>
                              )}
                              
                              {/* Show actions for sellers on buyer offers */}
                              {showSellerActions && (
                                <div className="mt-3 pt-2 border-t border-gray-300">
                                  <SellerOfferActions
                                    offer={offer}
                                    showCounter={counteringOfferId === offer._id}
                                    counterInputValue={counterInputValue}
                                    setCounterInputValue={setCounterInputValue}
                                    onOpenCounter={() => { setCounteringOfferId(offer._id); setCounterInputValue(""); }}
                                    onCloseCounter={() => { setCounteringOfferId(null); setCounterInputValue(""); }}
                                    onSubmitCounter={(price) => { handleSellerOfferResponse(offer._id, "countered", price); setCounteringOfferId(null); setCounterInputValue(""); }}
                                    onAccept={(offerId) => handleSellerOfferResponse(offerId, "accepted")}
                                    onReject={(offerId) => handleSellerOfferResponse(offerId, "rejected")}
                                  />
                                </div>
                              )}
                            </div>
                            <p className="text-[11px] text-gray-400">
                              {format(item.createdAt)}
                            </p>
                          </div>
                        </div>
                      );
                    }

                    if (item.isSystem) {
                      const sys = item.systemData;
                      const isOrderUpdate = sys && sys.type === "order_update" && sys.orderId;

                      if (!isOrderUpdate) {
                        return (
                          <div key={item._id || `sys-${item.createdAt}-${item.text}`} className="flex justify-center my-3">
                            <div className="max-w-[85%] rounded-xl border border-slate-200 bg-white px-4 py-3 text-center text-sm text-slate-700 shadow-sm">
                              <div className="font-medium text-slate-800">{item.text}</div>
                              {item.createdAt && (
                                <span className="mt-2 block text-xs text-slate-500">{format(item.createdAt)}</span>
                              )}
                            </div>
                          </div>
                        );
                      }

                      const { isBuyer: viewerIsOrderBuyer, isSeller: viewerIsOrderSeller } = inferOrderChatRole(
                        sys,
                        user,
                        currentChat
                      );
                      const visibleTo = String(sys?.visibleTo || "all");
                      if (visibleTo === "buyer" && !viewerIsOrderBuyer) return null;
                      if (visibleTo === "seller" && !viewerIsOrderSeller) return null;

                      const { headline, copy, orderHref } = getOrderUpdateDisplay(
                        sys,
                        user,
                        item.text,
                        currentChat
                      );
                      const summaryLine = sys?.summaryLine ? String(sys.summaryLine).trim() : "";
                      const eventKey = String(sys?.event || "");
                      const ribbonLabel =
                        eventKey === "placed"
                          ? "Order"
                          : eventKey === "dispatched"
                            ? "Shipping"
                            : eventKey === "delivered_prompt"
                              ? "Delivery"
                              : eventKey === "review_prompt"
                                ? "Review"
                                : eventKey === "refund_rejected" ||
                                  eventKey === "refund_return_required" ||
                                  eventKey === "refund_resolved"
                                ? "Refund"
                                : "Update";
                      const isDeliveredPrompt = sys?.event === "delivered_prompt";
                      const isReviewPrompt = sys?.event === "review_prompt";
                      const buyerIdNorm = normUserId(sys?.buyerId);
                      const sellerIdNorm = normUserId(sys?.sellerId);
                      const isOrderBuyerForActions = Boolean(buyerIdNorm && currentUserIdSet(user).has(buyerIdNorm));
                      const isOrderSellerForActions = Boolean(
                        sellerIdNorm && currentUserIdSet(user).has(sellerIdNorm)
                      );
                      const deliveredPromptActionable =
                        isDeliveredPrompt && isDeliveredPromptActionable(sys);
                      const deliveredPromptActions = deliveredPromptActionable
                        ? (sys.actions || []).filter(
                            (a) => a?.id === "accept" || a?.id === "raise_issue"
                          )
                        : [];
                      const showDeliveredActions =
                        isOrderUpdate &&
                        deliveredPromptActionable &&
                        isOrderBuyerForActions &&
                        deliveredPromptActions.length > 0;
                      const showReviewActions =
                        isOrderUpdate &&
                        isReviewPrompt &&
                        (isOrderBuyerForActions || isOrderSellerForActions) &&
                        Array.isArray(sys.actions) &&
                        sys.actions.length > 0;
                      const deliveredPromptPending =
                        isOrderUpdate &&
                        isDeliveredPrompt &&
                        isOrderBuyerForActions &&
                        !deliveredPromptActionable &&
                        !sys?.resolution;
                      const deliveredPromptEligibleLabel = formatDeliveredPromptEligibleDate(sys);
                      const resolutionNote =
                        isDeliveredPrompt && sys?.resolution
                          ? DELIVERED_RESOLUTION_COPY[sys.resolution] || null
                          : null;
                      const orderIdStr = sys?.orderId != null ? String(sys.orderId) : "";
                      const actionsLocked = Boolean(orderIdStr && deliveredPromptLockedOrders[orderIdStr]);
                      const displayHeadline =
                        deliveredPromptPending ? "Awaiting delivery" : headline;

                      return (
                        <div key={item._id || `sys-${item.createdAt}-${item.text}`} className="flex justify-center my-4">
                          <div className="w-full max-w-md overflow-hidden rounded-2xl border border-slate-200/90 bg-white text-left shadow-md ring-1 ring-slate-900/5">
                            <div className="border-b border-slate-100 bg-gradient-to-r from-[#f4f7f5] to-white px-4 py-2.5">
                              <span className="text-[11px] font-semibold uppercase tracking-wide text-[#38513b]">
                                {ribbonLabel}
                              </span>
                            </div>
                            <div className="px-4 py-3">
                              <h4 className="text-base font-semibold leading-snug text-slate-900">{displayHeadline}</h4>
                              {summaryLine ? (
                                <p className="mt-1.5 text-sm font-medium text-slate-800">{summaryLine}</p>
                              ) : null}
                              {copy ? (
                                <p className="mt-2 text-sm leading-relaxed text-slate-600">{copy}</p>
                              ) : null}
                              {resolutionNote && (
                                <div className="mt-3 rounded-lg border border-emerald-200/80 bg-emerald-50/90 px-3 py-2.5 text-sm font-medium text-emerald-900">
                                  {resolutionNote}
                                </div>
                              )}
                              {deliveredPromptPending && (
                                <p className="mt-3 rounded-lg border border-amber-200/80 bg-amber-50/90 px-3 py-2.5 text-sm text-amber-900">
                                  {deliveredPromptEligibleLabel
                                    ? `You can confirm receipt from ${deliveredPromptEligibleLabel} (based on the seller’s delivery estimate).`
                                    : "You can confirm receipt once the estimated delivery time has passed."}
                                </p>
                              )}
                              {orderHref ? (
                                <div className="mt-3">
                                  <Link
                                    to={orderHref}
                                    className="inline-flex min-h-[44px] w-full items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-[#38513b] shadow-sm transition hover:border-[#38513b] hover:bg-[#f6faf7] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#38513b] focus-visible:ring-offset-2"
                                  >
                                    View order
                                  </Link>
                                </div>
                              ) : null}
                              {showDeliveredActions && (
                                <>
                                  <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-stretch">
                                    {deliveredPromptActions.map((a) => {
                                      const id = a?.id;
                                      const label = a?.label || "Action";
                                      if (id === "accept") {
                                        return (
                                          <button
                                            key={id}
                                            type="button"
                                            disabled={actionsLocked}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              handleOrderAccept(sys.orderId);
                                            }}
                                            className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-xl bg-[#38513b] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[#2f4232] disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-slate-400 disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#38513b] focus-visible:ring-offset-2"
                                          >
                                            {label}
                                          </button>
                                        );
                                      }
                                      if (id === "raise_issue") {
                                        return (
                                          <button
                                            key={id}
                                            type="button"
                                            disabled={actionsLocked}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              handleOrderRaiseIssue(sys.orderId);
                                            }}
                                            className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-xl border-2 border-rose-400 bg-white px-4 text-sm font-semibold text-rose-700 shadow-sm transition hover:bg-rose-50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400 disabled:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 focus-visible:ring-offset-2"
                                          >
                                            {label}
                                          </button>
                                        );
                                      }
                                      return null;
                                    })}
                                  </div>
                                  <p className="mt-3 text-sm leading-relaxed text-slate-600">
                                    You have 2 days from delivery to respond before this order is automatically marked as
                                    complete
                                  </p>
                                </>
                              )}
                              {showReviewActions && (
                                <div className="mt-4 flex flex-col gap-2">
                                  {sys.actions.map((a) => {
                                    const id = a?.id;
                                    const label = a?.label || "Leave a review";
                                    if (id !== "leave_review") return null;
                                    const reviewHref = isOrderBuyerForActions
                                      ? `/profile/order/${sys.orderId}?review=1`
                                      : `/profile/seller-order/${sys.orderId}?review=1`;
                                    return (
                                      <Link
                                        key={id}
                                        to={reviewHref}
                                        className="inline-flex min-h-[44px] w-full items-center justify-center rounded-xl bg-[#38513b] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[#2f4232] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#38513b] focus-visible:ring-offset-2"
                                      >
                                        {label}
                                      </Link>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                            {item.createdAt && (
                              <div className="border-t border-slate-100 bg-slate-50/80 px-4 py-2">
                                <span className="text-xs text-slate-500">{format(item.createdAt)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    }

                    // Render regular message
                    const isMe = item.sender === user?._id;
                    return (
                      <div
                        key={item._id || item.createdAt}
                        className={`flex w-full ${
                          isMe ? "justify-end" : "justify-start"
                        }`}
                        ref={scrollRef}
                      >
                        <div className="max-w-[75%] space-y-1">
                          {item.images && (
                            <img
                              src={`${backend_url}${item.images}`}
                              alt="attachment"
                              className="w-full rounded-md"
                            />
                          )}
                          {item.text && (
                            <div
                              className={`px-4 py-2 rounded-lg text-sm leading-relaxed ${
                                isMe ? "bg-[#38513b] text-white" : "bg-gray-100 text-gray-900"
                              }`}
                            >
                              {item.text}
                            </div>
                          )}
                          <p className="text-[11px] text-gray-400">
                            {format(item.createdAt)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  </>
                  );
                })()}
              </div>

              {currentChat?.productId && !isSeller && !currentChat?.isAdminPriority && !hasOffersInCurrentConversation && (
                <div className="px-4 py-2 border-t border-gray-100 flex items-center gap-2 bg-gray-50/80">
                  <button
                    type="button"
                    onClick={() => setShowOfferModal(true)}
                    className="text-sm font-medium text-[#38513b] hover:text-[#2f4232] border border-[#38513b] rounded-md px-3 py-1.5 hover:bg-[#38513b]/10"
                  >
                    Send an offer
                  </button>
                  <span className="text-xs text-gray-500">
                    {currentChat.productId.name ? `for ${currentChat.productId.name}` : "for this product"}
                  </span>
                </div>
              )}
              <form
                onSubmit={sendMessageHandler}
                className="px-4 py-3 border-t border-gray-100 flex flex-wrap sm:flex-nowrap items-center gap-3"
              >
                <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-md p-2" title={`Upload image (Max size: ${IMAGE_UPLOAD_MAX_MB}MB, Recommended: 800x600px)`}>
                  <TfiGallery size={18} />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </label>
                <input
                  type="text"
                  placeholder="Type your message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  className="flex-1 min-w-0 rounded-md border border-gray-200 px-3 py-2 text-[16px] focus:outline-none focus:ring-2 focus:ring-[#38513b]"
                  style={{ fontSize: "16px" }}
                />
                <button
                  type="submit"
                  className="bg-[#38513b] hover:bg-[#2f4232] text-white rounded-md px-4 py-2 text-sm font-medium flex items-center gap-1 shrink-0 w-full sm:w-auto justify-center"
                >
                  Send
                  <AiOutlineSend size={16} />
                </button>
              </form>
              {showOfferModal && currentChat?.productId && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                  <div className="bg-white rounded-xl shadow-lg max-w-sm w-full p-5">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Send an offer</h3>
                    <p className="text-sm text-gray-600 mb-3">{currentChat.productId.name || "This product"}</p>
                    <form onSubmit={handleSendOfferFromInbox} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Your offer (GBP)</label>
                        <input
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={offerPrice}
                          onChange={(e) => setOfferPrice(e.target.value)}
                          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20"
                          placeholder={String(currentChat.productId.discountPrice ?? "")}
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                          onClick={() => { setShowOfferModal(false); setOfferPrice(""); }}
                          disabled={isSubmittingOffer}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="rounded-lg bg-[#38513b] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2f4232] disabled:opacity-60"
                          disabled={isSubmittingOffer}
                        >
                          {isSubmittingOffer ? "Sending..." : "Send offer"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-center p-10 text-gray-500">
              <div>
                <p className="text-lg font-semibold mb-1">Select a conversation</p>
                <p className="text-sm">
                  Choose a conversation from the list to start chatting with a seller.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InboxPanel;
